import { formatWhen } from '@/shared/common/formatWhen'

/**
 * W66 (§3.11 #7 · WSP-005) · The two cells the portfolio list gained: what
 * the book is worth as last measured, and what compliance currently says.
 *
 * Both render an honest absence. A book no run has valued shows "not
 * measured" with the reason, never 0; a book no rule has been evaluated on
 * shows the same, never "clear". RES-002 for a list: the compliant mark is
 * never shown without the not-evaluated count.
 */

const STATUS_TONE = {
  compliant: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  breach: 'bg-red-100 text-red-800',
  not_evaluated_data_quality: 'bg-slate-200 text-slate-700',
  not_evaluated_unsupported: 'bg-slate-200 text-slate-700',
  not_evaluated_system_error: 'bg-purple-100 text-purple-800',
  not_applicable: 'bg-slate-100 text-slate-500',
  pending_external_data: 'bg-blue-100 text-blue-800'
}

const STATUS_WORD = {
  compliant: 'Compliant',
  warning: 'Warning',
  breach: 'Breach',
  not_evaluated_data_quality: 'Not evaluated',
  not_evaluated_unsupported: 'Not evaluated',
  not_evaluated_system_error: 'Not evaluated',
  not_applicable: 'Not applicable',
  pending_external_data: 'Pending data'
}

const money = (value) => {
  const parsed = Number(value)
  return Number.isNaN(parsed) ? String(value ?? '—') : parsed.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

export const ValueCell = ({ valuation }) => {
  if (!valuation) return <span className="text-gray-400">—</span>
  if (!valuation.measured) {
    return <span className="text-xs text-gray-500" title={valuation.reason}>Not measured</span>
  }
  const unvalued = valuation.unvaluedCount ? ` · ${valuation.unvaluedCount} position(s) unvalued` : ''
  return (
    <span className="tabular-nums" title={`As of ${formatWhen(valuation.asOf)}${unvalued}`}>
      {money(valuation.nav)}{valuation.baseCurrency ? ` ${valuation.baseCurrency}` : ''}
      {valuation.sumOfSourceCurrencies && <span className="block text-[10px] text-amber-700 font-normal">sum of source currencies</span>}
    </span>
  )
}

export const ComplianceCell = ({ compliance }) => {
  if (!compliance) return <span className="text-gray-400">—</span>
  if (!compliance.measured) {
    return <span className="text-xs text-gray-500" title={compliance.reason}>Not measured</span>
  }
  const detail = [
    compliance.breach ? `${compliance.breach} breach` : null,
    compliance.warning ? `${compliance.warning} warning` : null,
    `${compliance.compliant} compliant`,
    `${compliance.notEvaluated} not evaluated`,
    compliance.notApplicable ? `${compliance.notApplicable} not applicable` : null
  ].filter(Boolean).join(' · ')
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${STATUS_TONE[compliance.worstStatus] || 'bg-slate-100 text-slate-600'}`}>
        {STATUS_WORD[compliance.worstStatus] || String(compliance.worstStatus || '').replaceAll('_', ' ')}
      </span>
      <span className="text-[10px] text-gray-500 font-normal whitespace-nowrap" title={`Evaluated ${formatWhen(compliance.evaluatedAt)}`}>{detail}</span>
    </span>
  )
}
